// serviceWorkerRegistration.js

// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://cra.link/PWA

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d?\d)){3}$/,
    ),
);

/**
 * Call this from your update prompt’s “Update now” button.
 * It will ask the waiting SW to activate and then reload immediately.
 */
export function applyUpdate(registration) {
  try {
    if (!registration) {
      // Fallback: just reload
      window.location.reload();
      return;
    }

    const waiter = registration.waiting || registration.installing || null;

    if (waiter) {
      // Ask the new SW to activate immediately
      waiter.postMessage({ type: 'SKIP_WAITING' });

      // Do not wait for `controllerchange`; reload right away for a snappy UX.
      // A tiny delay gives the browser a tick to swap controllers on fast devices.
      setTimeout(() => {
        window.location.reload();
      }, 50);
    } else {
      // Nothing to wait for; reload anyway.
      window.location.reload();
    }
  } catch (_e) {
    // If anything goes wrong, ensure the app reloads.
    window.location.reload();
  }
}

export function register(config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        // This is running on localhost. Check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config);

        // Add some additional logging to localhost, pointing developers to the
        // service worker/PWA documentation.
        navigator.serviceWorker.ready.then(() => {
          // console.log('Service worker is ready (localhost).');
        });
      } else {
        // Is not localhost. Just register service worker
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      // Whenever a new SW is found, track its lifecycle
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (!installingWorker) return;

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            // If there's an existing controller, then this is an update.
            if (navigator.serviceWorker.controller) {
              // 🔔 New content is available — let the app decide what to do.
              if (config && typeof config.onUpdate === 'function') {
                config.onUpdate(registration);
              }
            } else {
              // First install: content is cached for offline use.
              if (config && typeof config.onSuccess === 'function') {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch(() => {
      // No-op: ignore registration errors to avoid blocking app load.
    });
}

function checkValidServiceWorker(swUrl, config) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready
          .then((registration) => {
            registration.unregister().then(() => {
              window.location.reload();
            });
          })
          .catch(() => {
            // Best-effort fallback:
            window.location.reload();
          });
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      // No internet connection found. App is running in offline mode.
      // Do nothing here; your app can handle offline UI.
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch(() => {
        // swallow errors
      });
  }
}
